import { QueryKey as PackageQueryKey, Query, QueryClient } from '@tanstack/react-query';

import { Id, PagedParams } from './types/GeneralTypes';

export type QueryKey = PackageQueryKey;

export interface QueryMapKey {
  _root: string;
  paged: (params: PagedParams) => QueryKey;
  one: (id: string | number | null) => QueryKey;
  editableOne: (id: string | number) => QueryKey;
  list: () => QueryKey;
  as: (key: (object | string | number | undefined | null | unknown)[]) => QueryKey;
  root: () => QueryKey;
}

export function getQueryKey(root: string): QueryMapKey {
  return {
    _root: root,
    root: () => [root],
    paged: (params: PagedParams) => [root, 'paged', params],
    one: (id: string | number | null) => [root, id, 'one'],
    editableOne: (id: string | number) => [root, id, 'one', 'editable'],
    list: () => [root, 'list'],
    as: (key: (object | string | number | undefined | null | unknown)[]) => [root, ...key],
  };
}

// The route reads its id from the URL as a string, while the hooks get the number from the row.
function isQueryOf(id: Id) {
  return (query: Query) => String(query.queryKey[1]) === String(id);
}

// Removing them under a mounted view would refetch the deleted row before the navigation lands.
function removeOnceUnobserved(queryClient: QueryClient, key: QueryMapKey, id: Id) {
  const cache = queryClient.getQueryCache();
  const sweep = () => {
    const own = cache.findAll({ queryKey: key.root(), predicate: isQueryOf(id) });
    own.filter(query => query.getObserversCount() === 0).forEach(query => cache.remove(query));
    return own.some(query => query.getObserversCount() > 0);
  };

  if (!sweep()) {
    return;
  }
  const unsubscribe = cache.subscribe(event => {
    if (event.type === 'observerRemoved' && !sweep()) {
      unsubscribe();
    }
  });
}

export function invalidateAfterDelete(queryClient: QueryClient, key: QueryMapKey, id: Id) {
  const isOwn = isQueryOf(id);
  void queryClient.cancelQueries({ queryKey: key.root(), predicate: isOwn });
  removeOnceUnobserved(queryClient, key, id);
  return queryClient.invalidateQueries({
    queryKey: key.root(),
    predicate: query => !isOwn(query),
  });
}
